/**
 * The words of an owner's history (g110): who did what to which list or item,
 * and when, in ordinary language. Pure, so every sentence is tested apart from
 * the page that draws it.
 *
 * A sentence comes back as parts: plain text, and references to a list or an
 * item, which the page draws as escaped text inside a link to that entity's
 * own history. Stored text is never markup and never an instruction, and a
 * name here is whatever was stored, so nothing in this file trusts it.
 *
 * What it will not claim: that a session was a person (it was a credential
 * that was signed in), that a guest's name is who they are (it is their own
 * word), or that a name an app gave itself is checked (nobody checked it).
 */

import type {
  ActionSummary, ActorView, ChangeLine, ChangePayload, ChangeView, DueFacts, GroupView,
} from "../../api/src/shared/activity.js";
import { formatDue, todayIso } from "./format.js";

export type Ref = { ref: "list" | "item"; id: string; listId: string | null; label: string | null; fallback: string };
/** A name someone or something gave itself, drawn apart so its direction stays its own. */
export type Name = { name: string };
export type Part = string | Ref | Name;

/** What the page knows that a summary does not: a list's name as this device holds it now. */
export interface Lookup {
  listName(id: string): string | null;
}

/**
 * Rows as shown: one per action, except a group, which is one row however
 * many of its children a page holds. Clear completed and an assistant's batch
 * are each one thing a person did, and a row per child would bury it.
 */
export interface Row {
  action: ActionSummary;
  /** Every action the row stands for, newest first. */
  members: ActionSummary[];
  group: GroupView | null;
}

const FOLDED = new Set(["clear_completed", "mcp_batch"]);

export function fold(actions: readonly ActionSummary[]): Row[] {
  const rows: Row[] = [];
  const byGroup = new Map<string, Row>();
  for (const action of actions) {
    const group = action.group;
    if (group && FOLDED.has(group.kind)) {
      const held = byGroup.get(group.id);
      if (held) {
        held.members.push(action);
        // An assistant's batch names itself by its own action, which says what the whole batch was.
        if (action.kind === "batch.items") held.action = action;
        continue;
      }
      const row: Row = { action, members: [action], group };
      byGroup.set(group.id, row);
      rows.push(row);
      continue;
    }
    rows.push({ action, members: [action], group: null });
  }
  return rows;
}

/* ------------------------------------------------------------ who */

/** Who acted, as plainly as what is known allows, and no further. A stored name is a part of its own. */
export function actorParts(actor: ActorView): Part[] {
  const connection = actor.connection;
  if (connection && connection.kind !== "share_link") {
    if (actor.retired) return ["A connection of a deleted account"];
    if (connection.label && connection.claimed) return ["An app calling itself “", { name: connection.label }, "”"];
    if (connection.label) return [{ name: connection.label }];
    return [connection.kind === "api_token" ? "An API token" : "A connected app"];
  }
  switch (actor.kind) {
    case "session":
      if (actor.you) return ["You"];
      return actor.retired ? ["A deleted account"] : actor.name ? [{ name: actor.name }] : ["Someone"];
    case "share_guest":
      if (actor.retired) return ["A former guest"];
      return actor.name ? [{ name: actor.name }, " (guest)"] : ["A guest"];
    case "share_account":
      if (actor.you) return ["You"];
      return actor.retired ? ["A deleted account"] : actor.name ? [{ name: actor.name }] : ["A signed-in visitor"];
    case "system":
      return ["Trayflux"];
    default:
      return [actor.you ? "You" : "Someone"];
  }
}

/** Who acted, as plain text. */
export function actorPhrase(actor: ActorView): string {
  return plain(actorParts(actor));
}

/** How the action reached Trayflux, for the detail. */
export function connectionPhrase(actor: ActorView, channel: string): string {
  const connection = actor.connection;
  if (connection?.kind === "share_link") return "Through a shared list's link";
  if (connection?.kind === "api_token") {
    return connection.label && !actor.retired ? `Through the API token “${connection.label}”` : "Through an API token";
  }
  if (connection?.kind === "oauth") {
    if (!connection.label || actor.retired) return "Through a connected app";
    return connection.claimed
      ? `Through a connected app that calls itself “${connection.label}”. Trayflux did not check that name.`
      : `Through the connected app ${connection.label}`;
  }
  if (actor.kind === "system" || channel === "system") return "By Trayflux itself";
  return "From a device signed in to this account";
}

/* ------------------------------------------------------------ what */

const FIELD_WORDS: Readonly<Record<string, string>> = {
  text: "text", note: "note", due: "due date", quantity: "quantity", category: "category", repeat: "repeat",
  name: "name", kind: "type", orderBy: "order",
};

function joined(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * A cleared change (g126) has no name: its list or item was deleted, and its
 * content is no longer kept. It is "a since-deleted item", except where the
 * sentence already says it was deleted.
 */
function itemRef(line: ChangeLine | undefined, expired = line?.expired === true): Ref {
  return { ref: "item", id: line?.entityId ?? "", listId: line?.listId ?? null, label: line?.label ?? null, fallback: expired ? "a since-deleted item" : "an item" };
}

function listRef(id: string | null, label: string | null, expired = false): Ref | null {
  return id ? { ref: "list", id, listId: id, label, fallback: expired ? "a since-deleted list" : "a list" } : null;
}

/** A list by its id: its name on this device, or the newest name history gave it, or nothing to say. */
function namedList(id: string | null, lookup: Lookup, from?: ChangeLine): Ref | null {
  if (!id) return null;
  const label = lookup.listName(id) ?? (from?.entityType === "list" && from.entityId === id ? from.label : null);
  return label === null ? null : listRef(id, label);
}

function inList(list: Ref | null, word = "in"): Part[] {
  return list ? [` ${word} `, list] : [];
}

/** The sentence for one row, with its actor first and a full stop last. */
export function sentence(row: Row, lookup: Lookup): Part[] {
  const action = row.action;
  const what = row.group && FOLDED.has(row.group.kind) ? groupWords(row, lookup) : actionWords(action, lookup);
  const after = action.outcome === "unchanged" ? " Nothing changed." : "";
  return [...actorParts(action.actor), " ", ...what, ".", ...(after ? [after] : [])];
}

function groupWords(row: Row, lookup: Lookup): Part[] {
  const group = row.group!;
  const first = row.members.flatMap((member) => member.changes)[0];
  const list = namedList(first?.listId ?? null, lookup);
  const expected = group.expected ?? row.members.length;
  const parts: Part[] = [];
  if (group.kind === "clear_completed") {
    parts.push(`cleared ${plural(expected, "completed item")}`, ...inList(list, "from"));
  } else {
    const adds = row.members.filter((member) => member.kind !== "batch.items").every((member) => member.kind === "item.created");
    parts.push(adds ? `added ${plural(expected, "item")}` : `made ${plural(expected, "change")}`, ...inList(list, adds ? "to" : "in"));
  }
  const heard = group.applied + group.unchanged + group.refused;
  if (!group.finished) parts.push(`. ${heard} of ${expected} ${heard === 1 ? "has" : "have"} reached Trayflux so far`);
  if (group.refused > 0) parts.push(`. ${group.refused} ${group.refused === 1 ? "was" : "were"} refused`);
  return parts;
}

/** What the fields of an item change name, in words, leaving out what only bookkeeping moved. */
function fieldWords(fields: readonly string[]): string[] {
  return fields.filter((field) => field in FIELD_WORDS).map((field) => FIELD_WORDS[field]!);
}

function actionWords(action: ActionSummary, lookup: Lookup): Part[] {
  const line = action.changes[0];
  const extra = Math.max(0, action.changeCount - 1);
  if (!action.known) return ["made a change this version of Trayflux can’t describe"];
  switch (action.kind) {
    case "item.created":
      return ["added ", itemRef(line), ...inList(namedList(line?.listId ?? null, lookup), "to")];
    case "item.changed": {
      const facets = line?.facets ?? [];
      const words = fieldWords(line?.changedFields ?? []);
      const item = itemRef(line);
      if (facets.includes("completed") || facets.includes("reopened")) {
        const verb: Part[] = facets.includes("completed") ? ["completed ", item] : ["marked ", item, " not done"];
        return words.length ? [...verb, ` and changed its ${joined(words)}`] : verb;
      }
      if (facets.includes("category_fill") && !words.filter((word) => word !== "category").length) return ["filled in the category of ", item];
      if (words.length === 1 && words[0] === "note") return ["edited the note on ", item];
      if (words.length) return [`changed the ${joined(words)} of `, item];
      return ["changed ", item];
    }
    case "item.occurrence_completed":
      return ["completed an occurrence of ", itemRef(line)];
    case "item.occurrence_undone":
      return ["undid the last completion of ", itemRef(line)];
    case "item.moved":
      return ["moved ", itemRef(line), ...inList(namedList(line?.listId ?? null, lookup))];
    case "item.relisted":
      return ["moved ", itemRef(line), ...inList(namedList(line?.otherListId ?? null, lookup), "from"),
        ...inList(namedList(line?.listId ?? null, lookup), "to")];
    case "item.deleted":
      return ["deleted ", itemRef(line, false), ...inList(namedList(line?.listId ?? null, lookup), "from")];
    case "list.created":
      if (line?.expired) return ["created ", listRef(line.entityId, null, true) ?? "a list"];
      return ["created the list ", listRef(line?.entityId ?? null, line?.label ?? null, line?.expired) ?? "a list"];
    case "list.changed": {
      const words = fieldWords(line?.changedFields ?? []);
      const list = listRef(line?.entityId ?? null, line?.label ?? null, line?.expired) ?? "a list";
      if (words.length === 1 && words[0] === "name" && !line?.expired) return ["renamed a list to ", list];
      if (words.length === 1 && words[0] === "type") return ["changed the type of ", list];
      return words.length ? [`changed the ${joined(words)} of `, list] : ["changed ", list];
    }
    case "list.order_changed": {
      const list = listRef(line?.entityId ?? null, line?.label ?? null, line?.expired) ?? "a list";
      return ["changed how ", list, " is ordered", ...(extra ? [`. ${plural(extra, "blank category", "blank categories")} ${extra === 1 ? "was" : "were"} filled`] : [])];
    }
    case "list.moved":
      return ["moved ", listRef(line?.entityId ?? null, line?.label ?? null, line?.expired) ?? "a list", " in your lists"];
    case "list.deleted":
      return [line?.expired ? "deleted " : "deleted the list ", listRef(line?.entityId ?? null, line?.label ?? null) ?? "a list",
        ...(extra ? [` and ${plural(extra, "item")} in it`] : [])];
    case "list.imported":
      return ["imported ", listRef(line?.entityId ?? null, line?.label ?? null, line?.expired) ?? "a list", ...(extra ? [` with ${plural(extra, "item")}`] : [])];
    case "sharing.enabled":
      return ["turned on a share link for ", ...listOf(line, lookup)];
    case "sharing.disabled":
      return ["turned off a share link for ", ...listOf(line, lookup)];
    case "sharing.regenerated":
      return ["replaced a share link for ", ...listOf(line, lookup)];
    case "batch.items":
      return [`made ${plural(action.changeCount, "change")}`];
    default:
      return ["made a change this version of Trayflux can’t describe"];
  }
}

function listOf(line: ChangeLine | undefined, lookup: Lookup): Part[] {
  const list = namedList(line?.listId ?? null, lookup);
  return [list ?? "a list"];
}

/* ------------------------------------------------------------ when */

/** The time a row shows, and what it is: the device's claim, or when the server recorded it. */
export function shownTime(action: ActionSummary): { at: number; source: "device" | "server" } {
  if (action.occurredAt !== null && action.timeSource === "device" && action.timeFlag !== "implausible") {
    return { at: action.occurredAt, source: "device" };
  }
  return { at: action.recordedAt, source: "server" };
}

/** "14:05", "Yesterday, 14:05", "8 Oct, 14:05" or "8 Oct 2025, 14:05", in this device's zone. */
export function when(at: number, now: Date = new Date()): string {
  const date = new Date(at);
  const clock = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(date);
  const day = todayIso(date);
  const today = todayIso(now);
  if (day === today) return `Today, ${clock}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (day === todayIso(yesterday)) return `Yesterday, ${clock}`;
  const sameYear = date.getFullYear() === now.getFullYear();
  const label = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) }).format(date);
  return `${label}, ${clock}`;
}

/** The detail's lines about time: when it was done, when it arrived, and anything doubtful about either. */
export function timeLines(action: ActionSummary, now: Date = new Date()): string[] {
  const lines: string[] = [];
  const fromDevice = action.channel === "sync" || action.channel === "shared_sync";
  if (action.occurredAt !== null && action.timeSource === "device") {
    const zone = action.timeZone ? ` (${action.timeZone})` : "";
    lines.push(`Done on the device: ${when(action.occurredAt, now)}${zone}`);
    if (action.timeFlag === "implausible") lines.push("The device’s clock looked wrong, so the time Trayflux recorded it is shown instead.");
  } else if (fromDevice) {
    lines.push("The device did not say when this was done, so the time Trayflux recorded it is shown.");
  }
  lines.push(`Recorded by Trayflux: ${when(action.recordedAt, now)}`);
  if (action.coverage === "legacy_coalesced") lines.push("Made offline by an older version of Trayflux, which may have merged several changes into this one.");
  if (action.coverage === "legacy_no_operation_id") lines.push("It came without an id of its own, so if it was sent twice it would be recorded twice.");
  if (action.coverage === "pre_history_reversal") lines.push("This undid something done before history was recorded.");
  return lines;
}

/* ------------------------------------------------------------ the detail */

/** One field's change as the detail shows it. A note is shown whole, as text. */
export interface FieldLine {
  label: string;
  before: string;
  after: string;
  /** A long text, shown as a block on its own rather than inline. */
  block: boolean;
}

const DETAIL_LABELS: Readonly<Record<string, string>> = {
  text: "Text", note: "Note", due: "Due date", quantity: "Quantity", category: "Category", repeat: "Repeat",
  done: "Done", listId: "List", name: "Name", kind: "Type", orderBy: "Order", completedAt: "Completed",
};

function dueWords(due: DueFacts | null): string {
  if (!due) return "None";
  return formatDue({ date: due.date, time: due.time, tz: due.tz }, todayIso());
}

function valueWords(field: string, value: unknown, lookup: Lookup, repeating: boolean): string {
  if (value === null || value === undefined || value === "") return "None";
  switch (field) {
    case "done": return value ? "Done" : "Not done";
    case "due": return dueWords(value as DueFacts);
    case "listId": return lookup.listName(String(value)) ?? "Another list";
    case "kind": return value === "shopping" ? "Shopping" : value === "todo" ? "To-do" : "Other";
    case "orderBy": return value === "category" ? "By category" : "Your order";
    case "repeat": return String(value).replace(/^./, (first) => first.toUpperCase());
    case "completedAt": return `${when(Number(value))}${repeating ? " (last occurrence)" : ""}`;
    default: return String(value);
  }
}

/**
 * The field changes a payload holds, in words. Bookkeeping fields stay out;
 * the completion time is labelled "Completed" for an ordinary item and "Last
 * occurrence completed" for a repeating one, which rolls on instead of
 * staying ticked.
 */
export function fieldLines(change: ChangeView, lookup: Lookup): FieldLine[] {
  const payload = change.payload;
  if (!payload || !("fields" in payload) || !payload.fields) return [];
  const fields = payload.fields as Readonly<Record<string, { before: unknown; after: unknown } | undefined>>;
  const repeating = change.entityType === "item" && repeats(payload);
  const lines: FieldLine[] = [];
  for (const [field, diff] of Object.entries(fields)) {
    if (!diff || !(field in DETAIL_LABELS)) continue;
    const label = field === "completedAt" && repeating ? "Last occurrence completed" : DETAIL_LABELS[field]!;
    lines.push({
      label,
      before: valueWords(field, diff.before, lookup, repeating),
      after: valueWords(field, diff.after, lookup, repeating),
      block: field === "note",
    });
  }
  return lines;
}

function repeats(payload: ChangePayload): boolean {
  if ("occurrence" in payload && payload.occurrence) return true;
  const fields = "fields" in payload ? (payload.fields as Record<string, { before: unknown; after: unknown } | undefined>) : {};
  return Boolean(fields.repeat?.after ?? fields.repeat?.before) || ("snapshot" in payload && "repeat" in payload.snapshot && Boolean(payload.snapshot.repeat));
}

/** What a created or deleted row held, for the detail: its text and its note, whole. */
export function snapshotLines(change: ChangeView): FieldLine[] {
  const payload = change.payload;
  if (!payload || !("snapshot" in payload)) return [];
  const snapshot = payload.snapshot;
  if ("text" in snapshot) {
    return [
      { label: "Text", before: "", after: snapshot.text, block: false },
      ...(snapshot.note ? [{ label: "Note", before: "", after: snapshot.note, block: true }] : []),
    ];
  }
  if ("name" in snapshot) return [{ label: "Name", before: "", after: snapshot.name, block: false }];
  return [{ label: "Link", before: "", after: snapshot.mode === "edit" ? "Edit link" : "View link", block: false }];
}

/** A change's one-line heading in the detail. */
export function changeHeading(change: ChangeView): string {
  const nameless = change.entityType === "list" ? "A list" : change.entityType === "item" ? "An item" : "A share link";
  const name = change.label ?? (change.expired && change.entityType !== "share_grant" ? `A deleted ${change.entityType}` : nameless);
  if (change.unreadable) return `${name}: this version of Trayflux can’t read this change.`;
  const kind = change.changeKind === "created" ? "added" : change.changeKind === "deleted" ? "deleted" : change.changeKind === "ended" ? "ended" : "changed";
  return change.expired ? `${name}: ${kind}. Its content is no longer kept.` : `${name}: ${kind}`;
}

/** The detail's line for an occurrence: which due date was completed, and what came next. */
export function occurrenceLine(change: ChangeView): string | null {
  const payload = change.payload;
  if (!payload || !("occurrence" in payload) || !payload.occurrence) return null;
  const occurrence = payload.occurrence;
  const next = occurrence.nextDue ? ` Next due ${dueWords(occurrence.nextDue)}.` : "";
  return `Occurrence due ${dueWords(occurrence.due)}, repeating ${occurrence.repeat}.${next}`;
}

/** A group's results, as the detail says them, never implying the group was all or nothing. */
export function groupLines(group: GroupView): string[] {
  const lines = [`${plural(group.applied, "change")} made, ${group.unchanged} with nothing to change, ${group.refused} refused.`];
  if (!group.finished) lines.push(`${group.unresolved} not heard of yet. The group is still open.`);
  for (const refusal of group.refusals) lines.push(`Refused (${refusal.code}): ${refusal.count}`);
  return lines;
}

/** The text a reference is drawn with. */
export function refText(part: Ref): string {
  return part.label ?? part.fallback;
}

/** A whole sentence as plain text, for an accessible name or a test. */
export function plain(parts: readonly Part[]): string {
  return parts.map((part) => (typeof part === "string" ? part : "name" in part ? part.name : refText(part))).join("");
}
